/*
 * Ryan O'Dowd
 * 2025-12-19
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  Model,
  Q,
} from '@nozbe/watermelondb';
import {
  // @TODO: children,
  field,
  immutableRelation,
} from '@nozbe/watermelondb/decorators';
import {
  Associations,
} from '@nozbe/watermelondb/Model';
import Footnote from './Footnote';
import utils from './utils';

export default class VerseText extends Model {
  static table = 'verse_texts';
  static associations: Associations = {
    verse_texts: {type: 'has_many', foreignKey: 'verse_text_id'},
    translations: {type: 'belongs_to', key: 'translation_id'},
  };

  @field('verse_id') verseId;
  @field('verse_api_id') verseApiId;
  @field('book_id') bookId;
  @field('chapter_number') chapterNumber;
  @field('verse_number') verseNumber;
  @field('verse_text_id') verseTextId;
  @field('verse_text') verseText;
  @field('heading') heading;
  @field('translation_id') translationId;
  @field('range_start_verse_number') rangeStartVerseNumber;
  @field('range_end_verse_number') rangeEndVerseNumber;

  // @TODO: @children('footnotes') footnotes
  @immutableRelation('translations', 'translation_id') translation;

  static async upsertAll(db, rows) {
    const footnotesToInsert = [];
    const collection = db.get(this.table);
    const existingRecords = await collection.query(Q.where('verse_text_id', Q.oneOf(rows.map((row) => row.verse_text_id)))).fetch();
    const existingRecordsByVerseTextId = Object.fromEntries(existingRecords.map((record) => [record.verseTextId, record]));
    const footnotesToDelete = await db.get('footnotes').query(Q.where('verse_text_id', Q.oneOf(rows.map((row) => row.verse_text_id)))).fetch(); // @TODO: hardcoded

    await db.write(async () => {
      // delete missing footnotes
      // @NOTE: this needs to be separate write so that pending updates and deletes don't conflict in the batch
      await db.batch(footnotesToDelete.map((footnote) => footnote.prepareDestroyPermanently()));
    });

    await db.write(async () => {
      const updates = rows.map((row) => {
        const updateStmts = [];
        const {footnotes, ...rest} = row;

        // upsert verse texts
        updateStmts.push(utils.prepareUpsertStatement(collection, existingRecordsByVerseTextId[row.verse_text_id], rest));

        // upsert footnotes
        if (footnotes.length > 0) {
          footnotesToInsert.push(footnotes);
        }

        return updateStmts;
      });

      await db.batch(updates.flat(1));
    });

    await Footnote.upsertAll(db, footnotesToInsert.flat(1));
  }

  static async getAllByTranslationIdBookIdChapterNumber(db, translationId, bookId, chapterNumber) {
    return db.get(this.table).query(
      Q.where('translation_id', translationId),
      Q.where('book_id', bookId),
      Q.where('chapter_number', chapterNumber),
    ).fetch();
  }

  static async deleteAllByTranslationId(db, translationId) {
    const collection = db.get(this.table);
    const existingRecords = await collection.query(Q.where('translation_id', translationId)).fetch();
    await db.write(async () => {
      await db.batch(existingRecords.map((verseText) => verseText.prepareDestroyPermanently()));
    });

    await Footnote.deleteAllByVerseTextIds(db, existingRecords.map((verseText) => verseText.verseTextId));
  }

  static async numVersesDownloadedByTranslationIds(db, translationIds) { // @TODO: currently, usage of this is passing in translationIds from redux...i think that's okay for now, but i think i'd rather get them from watermelon?
    const collection = db.get(this.table);
    const counts = await Promise.all(
      translationIds.map(async (id) => ({
        translationId: id,
        numVersesDownloaded: await collection.query(Q.where('translation_id', id)).fetchCount(),
      })),
    );

    const result = counts.reduce((acc, curr) => {
      acc[curr.translationId] = curr.numVersesDownloaded;
      return acc;
    }, {});
    /* @TODO: loki doesn't currently support this
    const result = await collection.query(Q.unsafeSqlQuery(`
      SELECT translation_id, COUNT(*) AS count
      FROM verse_texts
      GROUP BY translation_id
    `)).fetch();
    */

    return result;
  }
};
